import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { Check, ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { control } from "./Field";

export type SelectOption = { value: string; label: string };
/** Options under a heading that cannot be chosen itself. */
export type SelectGroup = { label: string; options: SelectOption[] };

const isGroup = (o: SelectOption | SelectGroup): o is SelectGroup => "options" in o;

/**
 * A choice of one from a list, drawn by the app instead of by the device: a button that shows the chosen
 * option and opens the list as a menu. Built on the dropdown menu, non-modal for the reason `Menu` gives; the
 * dedicated Radix select injects a <style> element as well.
 */
export function Select({
  id,
  value,
  onChange,
  options,
  disabled,
  invalid,
  className,
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly (SelectOption | SelectGroup)[];
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}) {
  // A modal <dialog> lies in the top layer and makes the rest of the page inert: its lists open inside it.
  const [dialog, setDialog] = useState<HTMLElement | null>(null);
  const groupId = useId();
  const [open, setOpen] = useState(false);
  const chosen = options.flatMap((o) => (isGroup(o) ? o.options : [o])).find((o) => o.value === value);
  const item = (o: SelectOption) => (
    <DropdownMenu.RadioItem
      key={o.value}
      value={o.value}
      textValue={o.label}
      className="relative flex cursor-pointer items-center rounded-md py-1.5 pr-2 pl-7 text-sm text-ink outline-none select-none data-[highlighted]:bg-green-wash"
    >
      <DropdownMenu.ItemIndicator className="absolute left-2 grid place-items-center text-green-ink">
        <Check size={13} aria-hidden="true" />
      </DropdownMenu.ItemIndicator>
      {o.label}
    </DropdownMenu.RadioItem>
  );
  return (
    <DropdownMenu.Root modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button
          ref={(el) => setDialog(el?.closest("dialog") ?? null)}
          type="button"
          role="combobox"
          aria-expanded={open}
          id={id}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={clsx(
            control,
            "flex h-9 items-center justify-between gap-2 text-left disabled:cursor-default disabled:opacity-60 data-[state=open]:border-green",
            invalid ? "border-danger" : "border-line",
            className,
          )}
        >
          <span className={clsx("truncate", !chosen && "text-ink-3")}>{chosen ? chosen.label : "Choose…"}</span>
          <ChevronDown size={14} className="shrink-0 text-ink-3" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal container={dialog ?? undefined}>
        <DropdownMenu.Content
          aria-label={ariaLabel}
          align="start"
          sideOffset={4}
          className="z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-(--radix-dropdown-menu-trigger-width) overflow-y-auto rounded-lg border border-line bg-surface p-1 shadow-pop"
        >
          <DropdownMenu.RadioGroup value={value} onValueChange={onChange}>
            {options.map((o, i) =>
              isGroup(o) ? (
                <DropdownMenu.Group key={o.label} aria-labelledby={`${groupId}-${i}`}>
                  <DropdownMenu.Label
                    id={`${groupId}-${i}`}
                    className="px-2 pt-2 pb-1 text-xs font-semibold text-ink-3"
                  >
                    {o.label}
                  </DropdownMenu.Label>
                  {o.options.map(item)}
                </DropdownMenu.Group>
              ) : (
                item(o)
              ),
            )}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
